'use client';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useAuthStore } from '@/store/auth.store';
import { ALL_LANGS, normalizeToLang, type Lang } from './i18n';

/** Same key for the cookie (read by SSR) and localStorage (cross-tab / legacy). */
export const LANG_COOKIE = 'logixa_lang';

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

/** Persist the language to the cookie so the NEXT server render is already correct. */
export function writeLangCookie(lang: Lang) {
  if (typeof document === 'undefined') return;
  // 1 year, root path, Lax so it rides top-level navigations (SSR can read it).
  document.cookie = `${LANG_COOKIE}=${lang}; path=/; max-age=31536000; SameSite=Lax`;
}

function guessFromBrowser(): Lang | null {
  if (typeof navigator === 'undefined') return null;
  return normalizeToLang(navigator.language);
}

const I18nCtx = createContext<Lang>('DE');

/** The active UI language, resolved by the provider. */
export const useLangCtx = (): Lang => useContext(I18nCtx);

/**
 * Holds the active UI language. `initialLang` comes from the server (the
 * `logixa_lang` cookie read in the root layout), so the FIRST client render
 * matches SSR exactly — this is what kills the hydration mismatch that the
 * old localStorage-in-useEffect approach produced.
 *
 * After mount (never in the render path) we reconcile against the logged-in
 * user's preference → cookie → legacy localStorage → browser guess, and write
 * the resolved value back to the cookie + localStorage so the next SSR and
 * other tabs are already correct.
 */
export function I18nProvider({ initialLang, children, forced = false }: { initialLang: Lang; children: ReactNode; forced?: boolean }) {
  const fixed: Lang = ALL_LANGS.includes(initialLang) ? initialLang : 'DE';
  const userLang = useAuthStore((s) => s.user?.language);
  const [lang, setLang] = useState<Lang>(fixed);

  useEffect(() => {
    // `forced` = the language is pinned by the URL (a /[lang]/… server route).
    // Never reconcile from cookie/user, so SSR === client and the page stays
    // in its URL language. The switcher navigates between locale URLs instead.
    if (forced) return;
    const resolve = (): Lang => {
      let fromLs: string | null = null;
      try { fromLs = localStorage.getItem(LANG_COOKIE); } catch { /* private mode */ }
      return (
        normalizeToLang(userLang as string) ||
        normalizeToLang(readCookie(LANG_COOKIE)) ||
        normalizeToLang(fromLs) ||
        guessFromBrowser() ||
        'DE'
      );
    };

    const apply = () => {
      const next = resolve();
      setLang(next);
      writeLangCookie(next);
      try { localStorage.setItem(LANG_COOKIE, next); } catch { /* private mode */ }
    };
    apply();

    // Switcher in the same tab dispatches this; other tabs fire 'storage'.
    const onChange = () => apply();
    const onStorage = (e: StorageEvent) => { if (e.key === LANG_COOKIE) apply(); };
    window.addEventListener('logixa-lang-change', onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('logixa-lang-change', onChange);
      window.removeEventListener('storage', onStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLang]);

  return <I18nCtx.Provider value={lang}>{children}</I18nCtx.Provider>;
}
